/**
 * F-073 — Online leaderboard store.
 *
 * Talks to two Edge Functions (`issue-nonce`, `submit-score`) and the
 * `leaderboard_v1` view. Pure I/O — no UI, no localStorage. Caller is
 * expected to handle the offline branch (env missing → null client).
 *
 * The store is constructed with an injected client + signing key so
 * tests can swap a stub.
 */

import type { SupabaseClient } from "@supabase/supabase-js";

import { signScore } from "./score-signer.ts";

export interface LeaderboardRow {
  rank: number;
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  /** F-083 — ISO 3166-1 alpha-2 country code (e.g. "KR"). Null when unknown. */
  country: string | null;
  score: number;
  merges: number;
  durationMs: number;
  starForged: boolean;
  /** F-086 — best run's peak tier (0..8). Null for legacy rows. */
  peakTier: number | null;
  createdAt: string;
}

export interface SubmitInput {
  userId: string;
  score: number;
  merges: number;
  durationMs: number;
  starForged: boolean;
  /** F-086 — peak tier reached this run (0..8). */
  peakTier?: number;
}

export interface SubmitResult {
  placed: boolean;
  rank: number | null;
  bestScore: number;
}

export interface FetchOptions {
  /** When true, bypass cache and force a network fetch. */
  forceRefresh?: boolean;
}

export interface OnlineLeaderboardStore {
  fetchTop(limit?: number, options?: FetchOptions): Promise<LeaderboardRow[]>;
  fetchUserRank(
    userId: string,
    options?: FetchOptions,
  ): Promise<LeaderboardRow | null>;
  submit(input: SubmitInput): Promise<SubmitResult>;
  /** Test-only — wipes the in-memory cache. */
  invalidateCache(): void;
}

interface OnlineStoreOptions {
  client: SupabaseClient;
  signingKey: string;
  /** Cache TTL in ms. Default 5 minutes — saves Supabase quota + UX stability. */
  cacheTtlMs?: number;
  /** Test-only clock injection — defaults to Date.now. */
  now?: () => number;
}

/** F-074 — leaderboard cache TTL (5 min default). */
export const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;

interface NonceResponse {
  nonce: string;
  expires_at: string;
}

interface SubmitResponse {
  placed: boolean;
  rank: number | null;
  best_score: number;
}

interface RawRow {
  rank: number;
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  country: string | null;
  score: number;
  merges: number;
  duration_ms: number;
  star_forged: boolean;
  peak_tier: number | null;
  created_at: string;
}

function fromRaw(row: RawRow): LeaderboardRow {
  return {
    rank: row.rank,
    userId: row.user_id,
    displayName: row.display_name,
    avatarUrl: row.avatar_url,
    country: row.country ?? null,
    score: row.score,
    merges: row.merges,
    durationMs: row.duration_ms,
    starForged: row.star_forged,
    peakTier: row.peak_tier ?? null,
    createdAt: row.created_at,
  };
}

export function createOnlineLeaderboardStore(
  opts: OnlineStoreOptions,
): OnlineLeaderboardStore {
  const { client, signingKey } = opts;
  const ttl = opts.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS;
  const clock = opts.now ?? Date.now;

  // Cache layout: top is keyed by limit (different limits get distinct
  // entries — but we only ever call with 1000 in production, so this is
  // mostly a single-slot cache). userRank is keyed by user_id.
  const topCache = new Map<number, { rows: LeaderboardRow[]; ts: number }>();
  const userCache = new Map<
    string,
    { row: LeaderboardRow | null; ts: number }
  >();

  const isFresh = (ts: number): boolean => clock() - ts < ttl;

  const invalidateCache = (): void => {
    topCache.clear();
    userCache.clear();
  };

  const fetchTop = async (
    limit = 1000,
    options: FetchOptions = {},
  ): Promise<LeaderboardRow[]> => {
    const cap = Math.min(limit, 1000);
    if (!options.forceRefresh) {
      const hit = topCache.get(cap);
      if (hit && isFresh(hit.ts)) return hit.rows;
    }
    const { data, error } = await client
      .from("leaderboard_v1")
      .select(
        "rank, user_id, display_name, avatar_url, country, score, merges, duration_ms, star_forged, peak_tier, created_at",
      )
      .order("rank", { ascending: true })
      .limit(cap);
    if (error) throw new Error(`fetch_top_failed: ${error.message}`);
    const rows = ((data as RawRow[] | null) ?? []).map(fromRaw);
    topCache.set(cap, { rows, ts: clock() });
    return rows;
  };

  const fetchUserRank = async (
    userId: string,
    options: FetchOptions = {},
  ): Promise<LeaderboardRow | null> => {
    if (!options.forceRefresh) {
      const hit = userCache.get(userId);
      if (hit && isFresh(hit.ts)) return hit.row;
    }
    const { data, error } = await client
      .from("leaderboard_v1")
      .select(
        "rank, user_id, display_name, avatar_url, country, score, merges, duration_ms, star_forged, peak_tier, created_at",
      )
      .eq("user_id", userId)
      .maybeSingle();
    if (error) return null;
    const row = data ? fromRaw(data as RawRow) : null;
    userCache.set(userId, { row, ts: clock() });
    return row;
  };

  const submit = async (input: SubmitInput): Promise<SubmitResult> => {
    // 1. Claim nonce.
    const { data: nonceData, error: nonceErr } = await client.functions.invoke<
      NonceResponse
    >("issue-nonce", { body: {} });
    if (nonceErr || !nonceData?.nonce) {
      throw new Error(`nonce_failed: ${nonceErr?.message ?? "no_nonce"}`);
    }

    // 2. Sign locally — F-086 includes peakTier so the server can verify
    // the displayed tier wasn't tampered with.
    const signature = await signScore(signingKey, {
      userId: input.userId,
      score: input.score,
      merges: input.merges,
      durationMs: input.durationMs,
      ...(input.peakTier !== undefined ? { peakTier: input.peakTier } : {}),
      nonce: nonceData.nonce,
    });

    // 3. Submit.
    const { data: submitData, error: submitErr } = await client.functions
      .invoke<SubmitResponse>("submit-score", {
        body: {
          score: input.score,
          merges: input.merges,
          duration_ms: input.durationMs,
          star_forged: input.starForged,
          ...(input.peakTier !== undefined ? { peak_tier: input.peakTier } : {}),
          nonce: nonceData.nonce,
          client_signature: signature,
        },
      });
    if (submitErr || !submitData) {
      throw new Error(`submit_failed: ${submitErr?.message ?? "no_response"}`);
    }
    // F-074 — invalidate so the next fetch reflects the new row.
    invalidateCache();
    return {
      placed: submitData.placed,
      rank: submitData.rank,
      bestScore: submitData.best_score,
    };
  };

  return { fetchTop, fetchUserRank, submit, invalidateCache };
}
